<template>
  <div class="skinned-overview-card" :class="{ 'championship-variant': championshipVariant }" :style="cardStyle">
    <div class="card-bg" aria-hidden="true"></div>

    <!-- Category logo. Suppressed for WC26 (FIFA-licensed mark) and in the
         championship variant. -->
    <div v-if="!hideCategoryLogo && !championshipVariant" class="category-logo">
      <img :src="assets.icon" :alt="category" />
    </div>

    <!-- Status pill + share. Hidden entirely in preview / reveal mode — the
         character reveal and Settings preview aren't about the user's current
         standing, so the pill would just be noise. Also hidden in the
         championship variant (which shows its own "Winner" badge). -->
    <div v-if="!previewMode && !championshipVariant" class="status-row">
      <DisqualificationTooltip
        v-if="participantData.status === 'DISQUALIFIED'"
        :participant-id="participantData.participantId"
        position="bottom"
      >
        <span :class="['status-pill', statusInfo.color]">
          {{ $t(statusInfo.text) }}<i :class="['ml-2', statusInfo.icon]"></i>
        </span>
      </DisqualificationTooltip>
      <span v-else :class="['status-pill', statusInfo.color]">
        {{ $t(statusInfo.text) }}<i :class="['ml-2', statusInfo.icon]"></i>
      </span>
      <button class="share-icon" @click="toggleShareLink" aria-label="share">
        <i class="fa-regular fa-share-nodes"></i>
      </button>
    </div>

    <!-- Character shield -->
    <div class="character-shield">
      <svg class="shield-outline shield-outline-outer" viewBox="0 0 274 499" preserveAspectRatio="none" aria-hidden="true">
        <path :d="shieldPathD" fill="none" stroke="white" stroke-width="2" />
      </svg>
      <svg class="shield-outline shield-outline-inner" viewBox="0 0 274 499" preserveAspectRatio="none" aria-hidden="true">
        <path :d="shieldPathD" fill="none" stroke="white" stroke-width="2" />
      </svg>
      <div class="shield-clip">
        <img v-if="assets.charBackground" class="shield-bg-image" :src="assets.charBackground" alt="" />
        <div class="shield-bg-scrim" aria-hidden="true"></div>
        <img v-if="!bgRemovalInProgress && displayCharImage" class="char-image" :src="displayCharImage" :alt="skin.name" />
        <div v-if="bgRemovalInProgress" class="char-loading">
          <div class="char-loading-spinner"></div>
          <span class="special-text">Processing character…</span>
        </div>
        <div class="country-flag">
          <Flag v-if="characterCountryCode" :code="characterCountryCode" size="M" />
          <i v-else class="fa-regular fa-globe country-flag-fallback" aria-hidden="true"></i>
        </div>
        <div v-if="!championshipVariant" class="char-name">
          <div class="char-firstname special-text">{{ skin.firstName }}</div>
          <div class="char-lastname special-text">{{ (skin.lastName || skin.name || '').toUpperCase() }}</div>
          <div class="char-name-divider"></div>
        </div>
        <div v-if="!championshipVariant" class="shield-bottom">
          <div class="stats-pill">
            <span class="special-text">{{ rankingHeader }}: {{ formattedScore }}</span>
            <span class="special-text">{{ rank }}° {{ $t('atRank') }}</span>
          </div>
          <div class="real-name special-text">{{ realUserName }}</div>
          <div class="joined-with special-text">
            <b class="special-text">{{ $t('joinedWith') }}:</b>
            {{ participantData.brokerAccountIdentifier }}
            <span class="special-text" v-if="participantData.brokerName">{{ participantData.brokerName }}</span>
          </div>
        </div>

        <!-- Championship variant: "Winner" badge + skin name + score (Goals) box,
             trimmed of the rank pill / real name / broker line. -->
        <div v-if="championshipVariant" class="championship-bottom">
          <span class="championship-winner-pill">
            <i class="fa-regular fa-medal" aria-hidden="true"></i>
            {{ $t('winner') }}
          </span>
          <div class="championship-winner-name special-text">{{ skin.name || skin.lastName }}</div>
          <div v-if="scoreBoxValue" class="championship-score-box">
            <div class="championship-score-value special-text">{{ scoreBoxValue }}</div>
            <div v-if="scoreBoxLabel" class="championship-score-label special-text">{{ scoreBoxLabel }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Embedded awards — render on every breakpoint. The standalone TournamentAwards
         block in TournamentOverview is suppressed when the skin theme is on, so this
         is the only awards surface (no duplication risk). -->
    <div class="embedded-awards" v-if="shouldRenderAwardsCard && !championshipVariant">
      <TournamentAwardsCard
        :awards="awards"
        :partnerLogo="$store.state.tournament && $store.state.tournament.tournamentSettings ? $store.state.tournament.tournamentSettings.imageUrl : ''"
        :faqsUrl="$store.state.tournament && $store.state.tournament.tournamentSettings ? $store.state.tournament.tournamentSettings.faqsUrl : ''"
        :showAwards="shouldShowAwards"
        :showSponsor="shouldShowSponsor"
      />
    </div>

    <ShareLink
      v-if="showShareLink && !previewMode"
      :tournamentParticipant="participantData"
      :fundingChallengeTestId="1"
      @closeWindow="hideShareModal"
    />
  </div>
</template>

<script>
import CountriesData from '@/views/Register/countries';
import { getCategoryAssets } from '@/services/skins.service';
import { removeBackgroundCached } from '@/services/bgRemoval.service';
import shieldVector from '@/assets/skins_assets/char_vector.svg';
import TournamentAwardsCard from '@/components/TournamentAwardsCard.vue';
import DisqualificationTooltip from '@/components/DisqualificationTooltip.vue';
import ShareLink from '@/views/Funding/Simulation/components/ShareLinkTest.vue';

export default {
  name: 'SkinnedTournamentOverviewCard',
  components: {
    TournamentAwardsCard,
    DisqualificationTooltip,
    ShareLink,
  },
  props: {
    participantData: { type: Object, required: true },
    participants: { type: Array, default: () => [] },
    rankingType: { type: String, default: '' },
    shouldShowAwards: { type: Boolean, default: true },
    shouldShowSponsor: { type: Boolean, default: true },
    // Preview overrides — used by Settings tab to show a live sample without
    // mutating the real tournament state. When null, values fall back to store.
    themeCodeOverride: { type: String, default: null },
    awardsOverride: { type: Array, default: null },
    previewMode: { type: Boolean, default: false },
    tournamentClosed: { type: Boolean, default: false },
    // Championship variant: a trimmed version of the card used for the
    // championship winner. Hides the status/share row, category logo, rank
    // pill, real name, "joined with" broker line and awards; shows a "Winner"
    // badge + a caller-provided score box (used for the "{n} Goals" copy).
    // Defaults off so the standard tournament usage is untouched.
    championshipVariant: { type: Boolean, default: false },
    scoreBoxValue: { type: String, default: '' },
    scoreBoxLabel: { type: String, default: '' },
  },
  data() {
    return {
      countries: CountriesData,
      showShareLink: false,
      shieldVectorUrl: shieldVector,
      shieldPathD: 'M137.239 496.532L136.993 496.804L136.748 496.532L133.844 493.303C121.827 479.943 103.251 471.338 86.3349 464.105C72.3609 458.195 58.2321 452.751 43.9725 447.784L43.9609 447.78L43.9497 447.776C41.9881 447.081 39.9434 446.389 37.8523 445.681L37.8475 445.68C31.7293 443.609 25.2153 441.405 19.2383 438.6C12.9933 435.671 8.21934 431.946 5.1272 426.464C2.06435 421.033 1.0741 414.616 1.02018 407.307L0.98584 70.8647L5.46806 70.4026C16.4757 69.2679 22.0463 65.0705 32.0318 57.5464L32.1608 57.4493L32.6355 57.09C37.4795 53.4033 42.5192 49.0099 47.8882 44.3294L48.1126 44.1338C53.6873 39.2741 59.5849 34.1418 65.6955 29.4969C77.8346 20.2694 91.5033 12.4084 106.39 12.4444L109.934 12.4531L111.056 16.247C111.226 16.8214 111.553 17.6545 111.976 18.4691C112.426 19.3375 112.791 19.8088 112.919 19.9369C115.043 22.0548 116.716 22.5724 117.985 22.5813C119.354 22.5908 120.941 22.0087 122.799 20.6112C126.682 17.6896 130.257 12.3903 133.295 7.62932L136.48 2.63833L136.993 1.83301L137.507 2.63833L140.692 7.62932C143.73 12.3903 147.304 17.6896 151.188 20.6112C153.046 22.0087 154.633 22.5908 156.001 22.5813C157.27 22.5724 158.944 22.0548 161.068 19.9369C161.196 19.8088 161.561 19.3375 162.011 18.4691C162.434 17.6545 162.761 16.8214 162.931 16.247L164.052 12.4531L167.597 12.4444C182.484 12.4084 196.152 20.2694 208.291 29.4969C214.402 34.1418 220.3 39.2741 225.874 44.1338L226.099 44.3294C231.468 49.0099 236.507 53.4033 241.351 57.09L241.826 57.4493L241.955 57.5464C251.941 65.0705 257.511 69.2679 268.519 70.4026L273.001 70.8647L272.967 407.307C272.913 414.616 271.923 421.033 268.86 426.464C265.768 431.946 260.994 435.671 254.749 438.6C248.772 441.405 242.259 443.609 236.14 445.679L236.135 445.681C234.044 446.389 231.999 447.081 230.037 447.776L230.026 447.78L230.014 447.784C215.755 452.751 201.626 458.195 187.652 464.105C170.736 471.338 152.16 479.943 140.143 493.303L137.239 496.532Z',
      processedImage: null,
      bgRemovalInProgress: false,
    };
  },
  computed: {
    skin() {
      return this.participantData.skin || {};
    },
    displayCharImage() {
      return this.processedImage || this.skin.imageUrl;
    },
    category() {
      // Championship variant has no tournament in the store, so don't fall back
      // to it — that would borrow whatever tournament was last viewed and load
      // the wrong themed assets. Use only the explicit override / skin category.
      if (this.championshipVariant) {
        return this.themeCodeOverride || this.skin.category || null;
      }
      return this.themeCodeOverride
        || this.skin.category
        || this.$store.state.tournament?.tournamentSettings?.skinCategory
        || this.$store.state.tournament?.competitionSkinTheme?.code
        || null;
    },
    assets() {
      return getCategoryAssets(this.category) || {};
    },
    hideCategoryLogo() {
      // FIFA-licensed mark; see template comment.
      return String(this.category || '').toUpperCase() === 'WC26';
    },
    cardStyle() {
      const styles = { '--shield-mask': `url(${shieldVector})` };
      if (this.assets.cardBackground) {
        styles['--skin-bg-image'] = `url(${this.assets.cardBackground})`;
      }
      return styles;
    },
    awards() {
      if (Array.isArray(this.awardsOverride)) return this.awardsOverride;
      return this.$store.state.tournament?.awards || [];
    },
    shouldRenderAwardsCard() {
      return (this.shouldShowAwards && this.awards.length > 0) || this.shouldShowSponsor;
    },
    characterCountryCode() {
      const name = this.skin.country;
      if (!name) return null;
      const aliases = {
        'United Kingdom': 'GB-UKM',
        'United States': 'US',
        'South Korea': 'KR',
        'North Korea': 'KP',
        'Czech Republic': 'CZ',
        'Ivory Coast': 'CI',
        'Russia': 'RU',
      };
      if (aliases[name]) return aliases[name];
      const country = this.countries.find(
        (c) => c.name === name || c.name.startsWith(name),
      );
      return country ? country.code : null;
    },
    realUserName() {
      const p = this.participantData;
      const fromParticipant = [p.firstName, p.lastName].filter(Boolean).join(' ').trim();
      if (fromParticipant) return fromParticipant;
      const u = this.$store.state.user || {};
      return [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
    },
    rank() {
      if (this.participantData.place !== undefined && this.participantData.place !== null) {
        return this.participantData.place;
      }
      const idx = this.participants
        .filter((p) => p.status === 'ACTIVE')
        .findIndex((p) => p.participantId === this.participantData.participantId);
      return idx === -1 ? '---' : idx + 1;
    },
    rankingHeader() {
      const type = this.rankingType
        || this.$store.state.tournament?.tournamentSettings?.tournamentRankingType;
      switch (type) {
        case 'SWISET_RATIO': return this.$t('swisetRatio');
        case 'BALANCE': return this.$t('balance');
        case 'EQUITY_RT': return this.$t('equity');
        case 'PROFIT_PERCENTAGE': return this.$t('profit');
        case 'GROSS_LOSS': return this.$t('grossLoss');
        case 'PERFORMANCE_RATIO': return this.$t('performanceRatio');
        default: return this.$t('profit');
      }
    },
    formattedScore() {
      const value = this.participantData?.score;
      if (value === null || value === undefined) return '-';
      const type = this.rankingType
        || this.$store.state.tournament?.tournamentSettings?.tournamentRankingType
        || 'SWISET_RATIO';
      switch (type) {
        case 'BALANCE':
          return new Intl.NumberFormat(this.$i18n.locale).format(value.toFixed(2));
        case 'PROFIT_PERCENTAGE':
        case 'PERFORMANCE_RATIO':
          return `${value.toFixed(2)}%`;
        default:
          return value.toFixed(2);
      }
    },
    statusInfo() {
      const status = (this.participantData?.status || '').toLowerCase();
      // Pre-start: "Winning"/"Participating" imply a live competition, so
      // active participants get a neutral "Enrolled" tag until it starts
      // (SFB-1198). The no-enrolled class is the neutral gray pill.
      const startTime = this.$store.state.tournament?.startTime;
      const notStarted = startTime && new Date() < new Date(startTime);
      if (notStarted && (status === 'active' || status === 'participating')) {
        return { text: 'enrolled', color: 'no-enrolled', icon: 'fa-regular fa-circle-check' };
      }
      const tournamentEnded = new Date() > new Date(this.$store.state.tournament?.endTime);
      if (status === 'winner' || (status === 'active' && this.rank > 0 && this.rank <= 3 && tournamentEnded)) {
        return { text: 'winner', color: 'winning', icon: 'fa-regular fa-medal' };
      }
      if (status === 'active' && this.rank === 1) {
        return { text: 'winning', color: 'winning', icon: 'fa-regular fa-medal' };
      }
      if (this.tournamentClosed && (status === 'active' || status === 'participating')) {
        return { text: 'tournamentParticipationFinished', color: 'active', icon: 'fa-regular fa-flag-checkered' };
      }
      if (status === 'active' || status === 'participating') {
        return { text: 'participating', color: 'active', icon: 'fa-regular fa-gamepad' };
      }
      if (status === 'paused') {
        return { text: 'notConsistent', color: 'paused', icon: 'fa-regular fa-triangle-exclamation' };
      }
      if (status === 'disqualified') {
        return { text: 'disqualified', color: 'disqualified', icon: 'fa-regular fa-circle-exclamation' };
      }
      return { text: status, color: 'no-enrolled', icon: '' };
    },
  },
  methods: {
    toggleShareLink() {
      this.showShareLink = !this.showShareLink;
    },
    hideShareModal() {
      this.showShareLink = false;
    },
    async processSkinImage() {
      // Preview / reveal / championship contexts render the image straight from
      // the server without client-side bg removal — show the character instantly
      // instead of a 6-10s processing spinner.
      if (this.previewMode || this.championshipVariant) {
        this.processedImage = null;
        this.bgRemovalInProgress = false;
        return;
      }
      const src = this.skin?.sourceImage;
      this.processedImage = null;
      if (!src) return;
      this.bgRemovalInProgress = true;
      try {
        const dataUrl = await removeBackgroundCached(src);
        if (this.skin?.sourceImage !== src) return;
        this.processedImage = dataUrl || this.skin.imageUrl;
      } finally {
        this.bgRemovalInProgress = false;
      }
    },
  },
  watch: {
    'skin.sourceImage': {
      immediate: true,
      handler() {
        this.processSkinImage();
      },
    },
  },
};
</script>

<style lang="scss" scoped>
$skin-success: var(--v-mainGreen-base);
$skin-error: #C42A14;
$skin-warning: #FF8933;

.skinned-overview-card {
  position: relative;
  border-radius: 30px;
  overflow: hidden;
  padding: 20px 20px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  font-family: 'Avenir', sans-serif;
  background-color: #1b1b1b;
  isolation: isolate;
}

// Championship variant: drop the surrounding black card and show only the
// shield (its dark fill keeps the white overlay text legible).
.skinned-overview-card.championship-variant {
  background-color: transparent;
  padding: 0;
  gap: 0;
  border-radius: 0;
  overflow: visible;

  .card-bg { display: none; }
  .shield-clip { background-color: #14161b; }

  // Cap the shield width: the inner pieces mix %-based (char image) and
  // fixed-px (flag box, fonts) sizing tuned for ~240px, so a wider shield
  // widens the frame without scaling the contents, throwing off the centering.
  .character-shield { max-width: 240px; }
}

.card-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
  background-image: var(--skin-bg-image);
  background-size: cover;
  background-position: center;
  opacity: 0.5;
  border-radius: 30px;
  pointer-events: none;
}

.category-logo {
  position: relative;
  z-index: 1;
  height: 30px;
  display: flex;
  justify-content: center;

  img {
    height: 100%;
    width: auto;
    object-fit: contain;
  }
}

.status-row {
  position: relative;
  z-index: 1;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 8px;

  .status-pill {
    background-color: var(--v-mainBlack-base, #1b1b1b);
    color: #fff;
    padding: 8px 18px;
    font-size: 14px;
    font-weight: 500;
    border-top-right-radius: 20px;
    border-bottom-right-radius: 20px;
    margin-left: -20px;
    display: inline-flex;
    align-items: center;

    &.active { background-color: #c03131; }
    &.winning { background-color: $skin-success; }
    &.paused { background-color: $skin-warning; }
    &.disqualified { background-color: $skin-error; }
    &.no-enrolled { background-color: #585858; }
  }

  .share-icon {
    background: transparent;
    border: none;
    color: #fff;
    width: 36px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 5px;
    cursor: pointer;
    font-size: 18px;
  }
}

.character-shield {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 274px;
  aspect-ratio: 274 / 499;
  margin-top: 4px;

  .shield-outline {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 5;
  }

  .shield-outline-outer {
    transform: scale(1.04);
    opacity: 0.7;
  }

  .shield-outline-inner {
    transform: scale(1);
  }

  .shield-clip {
    position: absolute;
    inset: 0;
    -webkit-mask-image: var(--shield-mask);
    mask-image: var(--shield-mask);
    -webkit-mask-size: 100% 100%;
    mask-size: 100% 100%;
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-position: center;
    mask-position: center;
    overflow: hidden;
  }

  .shield-bg-image {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: 0;
  }

  .shield-bg-scrim {
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0) 45%,
      rgba(0, 0, 0, 0.75) 65%,
      rgba(0, 0, 0, 0.95) 75%,
      rgba(0, 0, 0, 100) 100%
    );
  }

  .char-image {
    position: absolute;
    top: 7%;
    left: 6%;
    width: 88%;
    height: 50%;
    object-fit: cover;
    object-position: center 20%;
    z-index: 1;
    -webkit-mask-image: linear-gradient(to bottom, #000 60%, transparent 100%);
    mask-image: linear-gradient(to bottom, #000 60%, transparent 100%);
  }

  .country-flag {
    position: absolute;
    top: 2.5%;
    left: 11%;
    z-index: 4;
    width: 48px;
    height: 86px;
    background: linear-gradient(180deg, #2d2d2d 0%, #0e0e0e 100%);
    border: 1.5px solid rgba(255, 255, 255, 0.55);
    border-top: none;
    border-radius: 0 0 12px 12px;
    padding: 0 0 14px 0;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.4);

    ::v-deep .flag {
      width: 32px !important;
      height: 24px !important;
      background-size: cover !important;
      background-position: center !important;
      border-radius: 2px;
      transform: none !important;
    }

    .country-flag-fallback {
      font-size: 20px;
      color: #f8f8f8;
      opacity: 0.85;
    }
  }

  .char-name {
    position: absolute;
    left: 0;
    right: 0;
    top: 48%;
    z-index: 4;
    text-align: center;
    pointer-events: none;
    padding: 0 22px;

    .char-firstname {
      font-family: 'Aeonik', 'Avenir', sans-serif;
      color: #c5c324;
      font-size: 18px;
      font-weight: 500;
      letter-spacing: 1px;
      line-height: 1;
    }

    .char-lastname {
      font-family: 'Aeonik', 'Avenir', sans-serif;
      color: #fff;
      font-size: 24px;
      font-weight: 700;
      letter-spacing: 1px;
      line-height: 1.1;
      margin-top: 2px;
    }

    .char-name-divider {
      height: 2px;
      background-color: #f8f8f8;
      border-radius: 2px;
      margin: 8px auto 0;
      width: 70%;
    }
  }

  .shield-bottom {
    position: absolute;
    left: 0;
    right: 0;
    top: 65%;
    bottom: 12%;
    padding: 0 22px;
    z-index: 4;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 6px;
  }

  .stats-pill {
    width: 100%;
    border: 1px solid rgba(248, 248, 248, 0.85);
    border-radius: 10px;
    padding: 5px 10px;
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    font-weight: 500;
    color: #f8f8f8;
  }

  .real-name {
    font-size: 18px;
    font-weight: 700;
    line-height: 1.15;
    color: #f8f8f8;
  }

  .joined-with {
    font-size: 11px;
    line-height: 1.25;
    color: #f8f8f8;
  }
}

// ── Championship variant (winner card) ──────────────────────────────────────
.championship-bottom {
  position: absolute;
  left: 0;
  right: 0;
  // Sit in the shield's wider mid-lower band — above the pointed tip — so the
  // score box isn't clipped by the silhouette taper.
  top: 44%;
  bottom: 22%;
  z-index: 4;
  padding: 0 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
}

.championship-winner-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  color: #ffc123;
  font-weight: 800;
  font-size: 14px;
  line-height: 1.5;
  padding: 3px 12px;
  border-radius: 18px;

  i { font-size: 13px; }
}

.championship-winner-name {
  font-family: 'Aeonik', 'Avenir', sans-serif;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.15;
  color: #f8f8f8;
}

.championship-score-box {
  width: 100%;
  border: 1px solid rgba(248, 248, 248, 0.85);
  border-radius: 10px;
  padding: 7px 10px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
}

.championship-score-value {
  font-family: 'Aeonik', 'Avenir', sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.5;
  color: #f8f8f8;
}

.championship-score-label {
  font-size: 12px;
  line-height: 1.4;
  color: #f8f8f8;
  opacity: 0.85;
}

.special-text {
  color: #f8f8f8 !important;
}

.char-loading {
  position: absolute;
  top: 20%;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  z-index: 3;
  font-size: 12px;
  color: #f8f8f8;
  pointer-events: none;
}

.char-loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(248, 248, 248, 0.25);
  border-top-color: #f8f8f8;
  border-radius: 50%;
  animation: char-loading-spin 0.9s linear infinite;
}

@keyframes char-loading-spin {
  to { transform: rotate(360deg); }
}

.embedded-awards {
  position: relative;
  z-index: 1;
  width: 100%;
  background-color: #f8f8f8;
  border: 1px solid #ebebeb;
  border-radius: 15px;
  padding: 16px 8px;

  ::v-deep .tournament-awards-card {
    border: none !important;
    background: transparent !important;
    padding: 0 !important;
    margin: 0 !important;
  }
}

// Dark-theme variant: dark surface + light border so global dark-theme text
// styles (which push text toward white) stay legible. Scoped via the Vuetify
// app-level `.theme--dark` class so the embedded wrapper tracks the page theme.
.theme--dark .embedded-awards {
  background-color: #1f1f1f;
  border-color: rgba(255, 255, 255, 0.12);
}

@media (max-width: 600px) {
  .skinned-overview-card {
    border-radius: 20px;
    padding: 16px;
  }

  .character-shield {
    max-width: 220px;
  }

  .character-info .real-name {
    font-size: 18px;
  }
}
</style>
